import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Badge, ExplorerProgress, GeoGuessResult, ThankYouNote, WorldObject } from '@/types'
import { BADGES } from '@/data/badges'
import { getAllWorldObjects, getWorldObjectById } from '@/services/landmarks'
import { getLevelInfo } from '@/data/levels'
import { trackEvent } from '@/services/analytics'
import { MINI_EXPEDITIONS } from '@/data/explorerJourneys'

const WEEK_MS = 7 * 24 * 60 * 60 * 1000
const WEEKEND_WANDERER_COUNT = 3
const COUNTRY_COLLECTOR_COUNT = 10
const CURIOUS_MIND_COUNT = 5
const QUIZ_CHAMPION_COUNT = 5
const QUIZ_BIG_PERFECT_LENGTH = 10
const GEO_GUESS_CHAMPION_COUNT = 5
const GEO_GUESS_SHARPSHOOTER_PERCENT = 90
const GEO_GUESS_THOUSAND_POINTS = 1000
const LETTER_GAME_SHARP_EYE_PERCENT = 92

interface ProgressState extends ExplorerProgress {
  /** Distinct places visited since the app loaded — resets every session on purpose, "one sitting" shouldn't persist across reloads/days. */
  sessionVisitedIds: string[]
  /** Badges earned during the most recent visitWorldObject()/markFactsRead() call, for celebratory UI. */
  newlyEarnedBadges: Badge[]
  /** Whether the Passport modal (ExplorerHud) is open — lifted here so other UI, like BadgeToast, can open it too. */
  passportOpen: boolean
  /** Whether the Quiz modal is open — same "lifted so it can be triggered from anywhere" reasoning as passportOpen. */
  quizOpen: boolean
  /** Whether the FAQ modal is open — same lifted-to-store reasoning as quizOpen/passportOpen (triggered from both HomePage's "?" button and the Passport's footer link). */
  faqOpen: boolean
  /** Whether the Feedback modal is open — same lifted-to-store reasoning as quizOpen/faqOpen/passportOpen (triggered from the global FeedbackButton, and from FaqModal/PassportModal's "send feedback" links). */
  feedbackOpen: boolean
  /** Thank-you notes fetched from Supabase that haven't been shown/dismissed yet — see FeedbackThankYouToast.tsx. Populated once per app session by App.tsx's startup check, not persisted itself (like newlyEarnedBadges). */
  pendingThankYous: ThankYouNote[]
  /** Ids of thank-you notes already shown & dismissed — persisted so a note never reappears after a reload, without needing any server-side "mark as read" write (thank_you_notes has no UPDATE policy for anon by design). */
  seenThankYouIds: string[]
  quizzesCompleted: number
  quizPerfectCount: number
  /** Whether the "Where in the World" overlay is open — same lifted-to-store reasoning as quizOpen/passportOpen. */
  geoGuessOpen: boolean
  geoGuessesCompleted: number
  /** Sessions where every round scored the full 3 stars. */
  geoGuessPerfectCount: number
  /** Cumulative sum of every round's percent score, across every session ever played — a lifetime tally, not a per-session one. */
  geoGuessTotalScore: number
  /** Completed 25-picture A to Z challenges; local-only like other learning progress. */
  letterGamesCompleted: number
  visitWorldObject: (worldObject: WorldObject) => void
  /** Called once a kid has scrolled through every fact section on a place — powers the Fact Finder badge. */
  markFactsRead: (worldObject: WorldObject) => void
  /** Called once per finished quiz (score out of total) — powers the quiz badges. */
  recordQuizResult: (score: number, total: number) => void
  /** Called once per finished "Where in the World" session — powers the geo-guess badges. */
  recordGeoGuessResult: (results: GeoGuessResult[]) => void
  recordLetterGameResult: (accuracyPercent: number) => void
  clearNewBadges: () => void
  /** Dismisses a single toast (its own close button) without touching any others still queued. */
  dismissBadge: (badgeId: string) => void
  openPassport: () => void
  closePassport: () => void
  openQuiz: () => void
  closeQuiz: () => void
  openFaq: () => void
  closeFaq: () => void
  openFeedback: () => void
  closeFeedback: () => void
  setPendingThankYous: (notes: ThankYouNote[]) => void
  /** Dismisses a single thank-you toast (its own close button) without touching any others still queued. */
  dismissThankYou: (id: string) => void
  openGeoGuess: () => void
  closeGeoGuess: () => void
}

/** Fires one unlock_achievement event per newly-earned badge — called from every award-granting method below rather than duplicating the loop at each call site. */
function trackNewBadges(badges: Badge[]) {
  for (const badge of badges) trackEvent('unlock_achievement', { achievement_id: badge.id })
}

/** Continent/theme/country badges require visiting every matching place — no partial credit, so re-check from scratch each time rather than tracking counts. */
function isCompletionBadgeEarned(badge: Badge, visitedIds: Set<string>, allObjects: WorldObject[]): boolean {
  const matching =
    badge.category === 'continent'
      ? allObjects.filter((o) => o.continent === badge.continent)
      : badge.category === 'theme'
        ? allObjects.filter((o) => o.tags.includes(badge.tag!))
        : badge.category === 'country'
          ? allObjects.filter((o) => o.country === badge.country)
          : []
  // A badge for an empty category (e.g. Antarctica today) should stay unearnable, not trivially true.
  return matching.length > 0 && matching.every((o) => visitedIds.has(o.id))
}

export const useProgressStore = create<ProgressState>()(
  persist(
    (set, get) => ({
      landmarksVisited: [],
      countriesDiscovered: [],
      lastVisitedAt: {},
      factsFullyReadIds: [],
      earnedBadgeIds: [],
      sessionVisitedIds: [],
      newlyEarnedBadges: [],
      passportOpen: false,
      quizOpen: false,
      faqOpen: false,
      feedbackOpen: false,
      pendingThankYous: [],
      seenThankYouIds: [],
      quizzesCompleted: 0,
      quizPerfectCount: 0,
      geoGuessOpen: false,
      geoGuessesCompleted: 0,
      geoGuessPerfectCount: 0,
      geoGuessTotalScore: 0,
      letterGamesCompleted: 0,
      completedExpeditionIds: [],

      visitWorldObject: (worldObject) => {
        const state = get()
        const now = Date.now()
        const alreadyVisited = state.landmarksVisited.includes(worldObject.id)
        const previousVisitAt = state.lastVisitedAt[worldObject.id]

        const landmarksVisited = alreadyVisited
          ? state.landmarksVisited
          : [...state.landmarksVisited, worldObject.id]
        const countriesDiscovered = state.countriesDiscovered.includes(worldObject.country)
          ? state.countriesDiscovered
          : [...state.countriesDiscovered, worldObject.country]
        const lastVisitedAt = { ...state.lastVisitedAt, [worldObject.id]: now }
        const sessionVisitedIds = state.sessionVisitedIds.includes(worldObject.id)
          ? state.sessionVisitedIds
          : [...state.sessionVisitedIds, worldObject.id]

        const visitedSet = new Set(landmarksVisited)
        const allObjects = getAllWorldObjects()
        const earnedBadgeIds = [...state.earnedBadgeIds]
        const newlyEarnedBadges: Badge[] = []

        function award(id: string) {
          if (earnedBadgeIds.includes(id)) return
          const badge = BADGES.find((b) => b.id === id)
          if (!badge) return
          earnedBadgeIds.push(id)
          newlyEarnedBadges.push(badge)
        }

        for (const badge of BADGES) {
          if (badge.category === 'milestone') continue
          if (isCompletionBadgeEarned(badge, visitedSet, allObjects)) award(badge.id)
        }

        if (!alreadyVisited && landmarksVisited.length === 1) {
          award('milestone-first-stamp')
        }

        const continentsWithPlaces = new Set(allObjects.map((o) => o.continent))
        const continentsVisited = new Set(
          Array.from(visitedSet)
            .map((id) => getWorldObjectById(id)?.continent)
            .filter((c): c is NonNullable<typeof c> => Boolean(c)),
        )
        if (continentsWithPlaces.size > 0 && continentsVisited.size >= continentsWithPlaces.size) {
          award('milestone-around-the-world')
        }

        if (sessionVisitedIds.length >= WEEKEND_WANDERER_COUNT) {
          award('milestone-weekend-wanderer')
        }

        if (previousVisitAt !== undefined && now - previousVisitAt >= WEEK_MS) {
          award('milestone-second-look')
        }

        if (countriesDiscovered.length >= COUNTRY_COLLECTOR_COUNT) {
          award('milestone-country-collector')
        }

        trackEvent('select_content', { content_type: 'place', item_id: worldObject.id, item_name: worldObject.name })
        const levelBefore = getLevelInfo(state.landmarksVisited.length, allObjects.length).level
        const levelAfter = getLevelInfo(landmarksVisited.length, allObjects.length).level
        if (levelAfter > levelBefore) trackEvent('level_up', { level: levelAfter })
        trackNewBadges(newlyEarnedBadges)

        const completedExpeditionIds = state.completedExpeditionIds.slice()
        for (const expedition of MINI_EXPEDITIONS) {
          if (!completedExpeditionIds.includes(expedition.id) && expedition.placeIds.every((id) => visitedSet.has(id))) {
            completedExpeditionIds.push(expedition.id)
            trackEvent('complete_expedition', { expedition_id: expedition.id })
          }
        }

        set({
          landmarksVisited,
          countriesDiscovered,
          lastVisitedAt,
          sessionVisitedIds,
          earnedBadgeIds,
          newlyEarnedBadges: [...state.newlyEarnedBadges, ...newlyEarnedBadges],
          completedExpeditionIds,
        })
      },


      markFactsRead: (worldObject) => {
        const state = get()
        if (state.factsFullyReadIds.includes(worldObject.id)) return

        const factsFullyReadIds = [...state.factsFullyReadIds, worldObject.id]
        const earnedBadgeIds = [...state.earnedBadgeIds]
        const newlyEarnedBadges: Badge[] = []

        function award(id: string) {
          if (earnedBadgeIds.includes(id)) return
          const badge = BADGES.find((b) => b.id === id)
          if (!badge) return
          earnedBadgeIds.push(id)
          newlyEarnedBadges.push(badge)
        }

        if (factsFullyReadIds.length === 1) award('milestone-fact-finder')
        if (factsFullyReadIds.length >= CURIOUS_MIND_COUNT) award('milestone-curious-mind')

        trackEvent('facts_complete', { item_id: worldObject.id, content_type: worldObject.type })
        trackNewBadges(newlyEarnedBadges)
        set({ factsFullyReadIds, earnedBadgeIds, newlyEarnedBadges: [...state.newlyEarnedBadges, ...newlyEarnedBadges] })
      },

      recordQuizResult: (score, total) => {
        const state = get()
        const quizzesCompleted = state.quizzesCompleted + 1
        const isPerfect = total > 0 && score === total
        const quizPerfectCount = state.quizPerfectCount + (isPerfect ? 1 : 0)
        const earnedBadgeIds = [...state.earnedBadgeIds]
        const newlyEarnedBadges: Badge[] = []

        function award(id: string) {
          if (earnedBadgeIds.includes(id)) return
          const badge = BADGES.find((b) => b.id === id)
          if (!badge) return
          earnedBadgeIds.push(id)
          newlyEarnedBadges.push(badge)
        }

        award('milestone-quiz-first')
        if (isPerfect) award('milestone-quiz-perfect')
        if (isPerfect && total >= QUIZ_BIG_PERFECT_LENGTH) award('milestone-quiz-big-perfect')
        if (quizzesCompleted >= QUIZ_CHAMPION_COUNT) award('milestone-quiz-five')

        trackEvent('quiz_complete', { score, total, is_perfect: isPerfect })
        trackNewBadges(newlyEarnedBadges)
        set({
          quizzesCompleted,
          quizPerfectCount,
          earnedBadgeIds,
          newlyEarnedBadges: [...state.newlyEarnedBadges, ...newlyEarnedBadges],
        })
      },

      recordGeoGuessResult: (results) => {
        const state = get()
        const geoGuessesCompleted = state.geoGuessesCompleted + 1
        const totalStars = results.reduce((sum, r) => sum + r.stars, 0)
        const maxStars = results.length * 3
        const isPerfect = results.length > 0 && totalStars === maxStars
        const geoGuessPerfectCount = state.geoGuessPerfectCount + (isPerfect ? 1 : 0)
        const sessionScore = results.reduce((sum, r) => sum + r.percent, 0)
        const geoGuessTotalScore = state.geoGuessTotalScore + sessionScore
        const bestSinglePercent = results.reduce((best, r) => Math.max(best, r.percent), 0)
        const earnedBadgeIds = [...state.earnedBadgeIds]
        const newlyEarnedBadges: Badge[] = []

        function award(id: string) {
          if (earnedBadgeIds.includes(id)) return
          const badge = BADGES.find((b) => b.id === id)
          if (!badge) return
          earnedBadgeIds.push(id)
          newlyEarnedBadges.push(badge)
        }

        award('milestone-geo-first')
        if (isPerfect) award('milestone-geo-perfect')
        if (geoGuessesCompleted >= GEO_GUESS_CHAMPION_COUNT) award('milestone-geo-five')
        if (bestSinglePercent >= GEO_GUESS_SHARPSHOOTER_PERCENT) award('milestone-geo-sharpshooter')
        if (geoGuessTotalScore >= GEO_GUESS_THOUSAND_POINTS) award('milestone-geo-thousand')

        trackEvent('geo_guess_complete', { rounds: results.length, total_stars: totalStars, is_perfect: isPerfect })
        trackNewBadges(newlyEarnedBadges)
        set({
          geoGuessesCompleted,
          geoGuessPerfectCount,
          geoGuessTotalScore,
          earnedBadgeIds,
          newlyEarnedBadges: [...state.newlyEarnedBadges, ...newlyEarnedBadges],
        })
      },

      recordLetterGameResult: (accuracyPercent) => {
        const state = get()
        const letterGamesCompleted = state.letterGamesCompleted + 1
        const earnedBadgeIds = [...state.earnedBadgeIds]
        const newlyEarnedBadges: Badge[] = []

        function award(id: string) {
          if (earnedBadgeIds.includes(id)) return
          const badge = BADGES.find((badge) => badge.id === id)
          if (!badge) return
          earnedBadgeIds.push(id)
          newlyEarnedBadges.push(badge)
        }

        award('milestone-letter-first')
        if (accuracyPercent >= LETTER_GAME_SHARP_EYE_PERCENT) award('milestone-letter-sharp-eye')
        if (accuracyPercent === 100) award('milestone-letter-perfect')

        trackNewBadges(newlyEarnedBadges)
        set({
          letterGamesCompleted,
          earnedBadgeIds,
          newlyEarnedBadges: [...state.newlyEarnedBadges, ...newlyEarnedBadges],
        })
      },

      clearNewBadges: () => set({ newlyEarnedBadges: [] }),
      dismissBadge: (badgeId) =>
        set((state) => ({ newlyEarnedBadges: state.newlyEarnedBadges.filter((b) => b.id !== badgeId) })),
      openPassport: () => set({ passportOpen: true }),
      closePassport: () => set({ passportOpen: false }),
      openQuiz: () => set({ quizOpen: true }),
      closeQuiz: () => set({ quizOpen: false }),
      openFaq: () => set({ faqOpen: true }),
      closeFaq: () => set({ faqOpen: false }),
      openFeedback: () => set({ feedbackOpen: true }),
      closeFeedback: () => set({ feedbackOpen: false }),
      setPendingThankYous: (notes) =>
        set((state) => ({ pendingThankYous: notes.filter((n) => !state.seenThankYouIds.includes(n.id)) })),
      dismissThankYou: (id) =>
        set((state) => ({
          pendingThankYous: state.pendingThankYous.filter((n) => n.id !== id),
          seenThankYouIds: [...state.seenThankYouIds, id],
        })),
      openGeoGuess: () => set({ geoGuessOpen: true }),
      closeGeoGuess: () => set({ geoGuessOpen: false }),
    }),
    {
      name: 'kaleido-explorer-progress',
      // sessionVisitedIds (a fresh "sitting" every load), newlyEarnedBadges
      // and pendingThankYous (one-shot toast queues), and every *Open flag
      // are intentionally excluded from persistence.
      partialize: (state) => ({
        landmarksVisited: state.landmarksVisited,
        countriesDiscovered: state.countriesDiscovered,
        lastVisitedAt: state.lastVisitedAt,
        factsFullyReadIds: state.factsFullyReadIds,
        earnedBadgeIds: state.earnedBadgeIds,
        completedExpeditionIds: state.completedExpeditionIds,
        quizzesCompleted: state.quizzesCompleted,
        quizPerfectCount: state.quizPerfectCount,
        geoGuessesCompleted: state.geoGuessesCompleted,
        geoGuessPerfectCount: state.geoGuessPerfectCount,
        geoGuessTotalScore: state.geoGuessTotalScore,
        letterGamesCompleted: state.letterGamesCompleted,
        seenThankYouIds: state.seenThankYouIds,
      }),
    },
  ),
)
